import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import SettingsPageLayout from './SettingsPageLayout';
import { enhancedApi } from '../../api';

const TEMPLATE_TYPES = [
  { value: 'invoice_reminder', label: 'Invoice' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'document_request', label: 'Document Request' },
  { value: 'custom', label: 'Custom' },
];

const VARIABLES = [
  '{{client_name}}',
  '{{invoice_amount}}',
  '{{due_date}}',
  '{{firm_name}}',
  '{{document_link}}',
  '{{portal_link}}',
];

const SAMPLE_DATA = {
  '{{client_name}}': 'John Smith',
  '{{invoice_amount}}': '$1,500.00',
  '{{due_date}}': 'Mar 31, 2025',
  '{{firm_name}}': 'Acme Tax & Advisory',
  '{{document_link}}': 'https://example.com/doc/123',
  '{{portal_link}}': 'https://portal.example.com',
};

const EmailTemplatesSettings = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [typeTab, setTypeTab] = useState('invoice_reminder');
  const [selectedId, setSelectedId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'invoice_reminder',
    subject: '',
    body: '',
    is_default: false,
  });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const bodyRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await enhancedApi.getEmailTemplates();
      const raw = res.data?.templates ?? res.data;
      const arr = Array.isArray(raw) ? raw : [];
      setTemplates(arr);
      if (arr.length === 0) {
        setSelectedId(null);
        setIsCreatingNew(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    const filtered = templates.filter((t) => t.template_type === typeTab);
    const currentInTab = selectedId && filtered.some((t) => t.id === selectedId);
    if (filtered.length === 0) {
      setSelectedId(null);
      setIsCreatingNew(false);
      setFormData((prev) => ({ ...prev, template_type: typeTab, name: '', subject: '', body: '', is_default: false }));
    } else if (!currentInTab && selectedId) {
      setSelectedId(filtered[0].id);
    }
    setPreviewMode(false);
    setPreviewHtml(null);
  }, [typeTab, templates]);

  useEffect(() => {
    if (isCreatingNew) {
      setFormData({
        name: '',
        template_type: typeTab,
        subject: '',
        body: '',
        is_default: false,
      });
    } else if (selectedId) {
      const t = templates.find((x) => x.id === selectedId);
      if (t) {
        setFormData({
          name: t.name,
          template_type: t.template_type,
          subject: t.subject,
          body: t.body ?? t.content ?? '',
          is_default: t.is_default ?? false,
        });
      }
    }
  }, [selectedId, isCreatingNew, typeTab, templates]);

  const filteredTemplates = templates.filter((t) => t.template_type === typeTab);
  const showEditor = selectedId !== null || isCreatingNew;

  const handleInsertVariable = (variable) => {
    const field = bodyRef.current;
    if (field) {
      const start = field.selectionStart ?? 0;
      const end = field.selectionEnd ?? 0;
      const before = formData.body.slice(0, start);
      const after = formData.body.slice(end);
      setFormData((prev) => ({ ...prev, body: before + variable + after }));
      setTimeout(() => field.focus(), 0);
    } else {
      setFormData((prev) => ({ ...prev, body: (prev.body || '') + variable }));
    }
  };

  const getPreviewHtml = useCallback(() => {
    if (selectedId && previewHtml) return previewHtml;
    let html = formData.body || '';
    VARIABLES.forEach((v) => {
      html = html.replace(new RegExp(v.replace(/[{}]/g, '\\$&'), 'g'), SAMPLE_DATA[v] ?? v);
    });
    return html;
  }, [formData.body, selectedId, previewHtml]);

  useEffect(() => {
    if (previewMode && selectedId && !previewHtml) {
      const load = async () => {
        try {
          setPreviewLoading(true);
          const res = await enhancedApi.previewEmailTemplate(selectedId);
          setPreviewHtml(res.data?.rendered_body ?? res.data?.body ?? '');
        } catch {
          setPreviewHtml(formData.body || '');
        } finally {
          setPreviewLoading(false);
        }
      };
      load();
    } else if (!previewMode) {
      setPreviewHtml(null);
    }
  }, [previewMode, selectedId]);

  const handleSave = async () => {
    if (!formData.name?.trim() || !formData.subject?.trim() || !formData.body?.trim()) {
      setError('Name, subject, and body are required.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      if (selectedId) {
        await enhancedApi.updateEmailTemplate(selectedId, formData);
        setSuccess('Template updated.');
      } else {
        const res = await enhancedApi.createEmailTemplate(formData);
        setSelectedId(res.data?.id);
        setIsCreatingNew(false);
        setSuccess('Template created.');
      }
      await fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, id) => {
    e?.stopPropagation?.();
    const targetId = id ?? selectedId;
    if (!targetId || !window.confirm('Delete this template?')) return;
    try {
      setSaving(true);
      setError(null);
      await enhancedApi.deleteEmailTemplate(targetId);
      if (selectedId === targetId) {
        setSelectedId(null);
        setIsCreatingNew(false);
      }
      setSuccess('Template deleted.');
      await fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete template');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedId) return;
    try {
      setTestSending(true);
      setError(null);
      await enhancedApi.sendTestEmailTemplate(selectedId);
      setSuccess('Test email sent.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setTestSending(false);
    }
  };

  const handleNewTemplate = () => {
    setSelectedId(null);
    setIsCreatingNew(true);
    setFormData({
      name: '',
      template_type: typeTab,
      subject: '',
      body: '',
      is_default: false,
    });
    setPreviewMode(false);
    setPreviewHtml(null);
  };

  const handleSelectTemplate = (id) => {
    setSelectedId(id);
    setIsCreatingNew(false);
  };

  return (
    <SettingsPageLayout
      title="Email Templates"
      description="Create and manage email templates for invoices, welcome messages, document requests, and more."
      maxWidth="none"
      noCard
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          flexDirection: { xs: 'column', md: 'row' },
          height: { md: 'calc(100vh - 180px)' },
          minHeight: 500,
        }}
      >
        {/* LEFT PANEL — 35% */}
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', md: '35%' },
            maxWidth: { md: 420 },
            flexShrink: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={typeTab}
            onChange={(_, v) => setTypeTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 48 }}
          >
            {TEMPLATE_TYPES.map((t) => (
              <Tab key={t.value} label={t.label} value={t.value} sx={{ minHeight: 48, py: 1.5 }} />
            ))}
          </Tabs>

          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="text" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="text" height={48} sx={{ mb: 1 }} />
              </Box>
            ) : (
              <List dense disablePadding sx={{ flex: 1 }}>
                {filteredTemplates.map((t) => (
                  <ListItemButton
                    key={t.id}
                    onClick={() => handleSelectTemplate(t.id)}
                    selected={selectedId === t.id}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        '&:hover': { bgcolor: 'primary.light' },
                      },
                    }}
                  >
                    <ListItemText
                      primary={t.name}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondary={
                        t.is_default ? (
                          <Chip label="Default" size="small" color="primary" variant="outlined" sx={{ mt: 0.5, height: 20 }} />
                        ) : null
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectTemplate(t.id);
                      }}
                      sx={{ color: 'text.secondary' }}
                      aria-label="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => handleDelete(e, t.id)}
                      disabled={saving}
                      aria-label="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                ))}
              </List>
            )}

            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleNewTemplate}
                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
              >
                New Template
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* RIGHT PANEL — 65% */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            borderLeft: { md: 'none' },
            borderTopLeftRadius: { md: 0 },
            borderBottomLeftRadius: { md: 0 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {!showEditor ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                Select a template to edit
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 320 }}>
                Choose a template from the list or create a new one to get started.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleNewTemplate}
                sx={{ mt: 2, textTransform: 'none' }}
              >
                New Template
              </Button>
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {error && (
                <Alert severity="error" sx={{ m: 2, mb: 0 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ m: 2, mb: 0 }} onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Template name"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  required
                  inputProps={{ maxLength: 100 }}
                  size="medium"
                  fullWidth
                  placeholder="Enter template name"
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '1.25rem', fontWeight: 600 },
                  }}
                />

                <TextField
                  label="Subject"
                  value={formData.subject}
                  onChange={(e) => setFormData((p) => ({ ...p, subject: e.target.value }))}
                  required
                  inputProps={{ maxLength: 200 }}
                  size="small"
                  fullWidth
                />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Variables — click to insert
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {VARIABLES.map((v) => (
                      <Chip
                        key={v}
                        label={v}
                        size="small"
                        variant="outlined"
                        onClick={() => handleInsertVariable(v)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>

                  <ToggleButtonGroup
                    value={previewMode ? 'preview' : 'edit'}
                    exclusive
                    onChange={(_, v) => v != null && setPreviewMode(v === 'preview')}
                    size="small"
                    sx={{ mb: 1 }}
                  >
                    <ToggleButton value="edit">
                      <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Edit
                    </ToggleButton>
                    <ToggleButton value="preview" disabled={!formData.body?.trim()}>
                      <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                      Preview
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {previewMode ? (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: 'background.paper',
                        minHeight: 300,
                        overflow: 'auto',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: previewLoading
                          ? '<p style="color: var(--mui-palette-text-disabled);">Loading preview...</p>'
                          : getPreviewHtml(),
                      }}
                    />
                  ) : (
                    <TextField
                      inputRef={bodyRef}
                      value={formData.body}
                      onChange={(e) => setFormData((p) => ({ ...p, body: e.target.value }))}
                      multiline
                      minRows={12}
                      fullWidth
                      size="small"
                      placeholder="Enter email body. Use variables like {{client_name}} for personalization."
                      sx={{
                        '& .MuiInputBase-root': { minHeight: 300 },
                      }}
                    />
                  )}
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_default}
                      onChange={(e) => setFormData((p) => ({ ...p, is_default: e.target.checked }))}
                    />
                  }
                  label="Set as Default"
                />
              </Box>

              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                {selectedId && (
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={handleSendTest}
                    disabled={testSending}
                    sx={{ textTransform: 'none' }}
                  >
                    {testSending ? 'Sending...' : 'Send Test Email'}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </SettingsPageLayout>
  );
};

// ---------------------------------------------------------------------------
// Compose Email Templates section (used by ComposeEmailDialog)
// ---------------------------------------------------------------------------

const COMPOSE_CATEGORIES = [
  { value: 'document_request', label: 'Document Request' },
  { value: 'status_update', label: 'Status Update' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'custom', label: 'Custom' },
];

function ComposeTemplateForm({ tpl, onSave, onDelete, onCancel }) {
  const [name, setName] = React.useState(tpl?.name || '');
  const [subjectTemplate, setSubjectTemplate] = React.useState(tpl?.subject_template || '');
  const [bodyTemplate, setBodyTemplate] = React.useState(tpl?.body_template || '');
  const [category, setCategory] = React.useState(tpl?.category || 'custom');
  const [saving, setSaving] = React.useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !subjectTemplate.trim() || !bodyTemplate.trim()) return;
    setSaving(true);
    try {
      await onSave({ name: name.trim(), subject_template: subjectTemplate.trim(), body_template: bodyTemplate, category });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField label="Template Name" size="small" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
      <TextField
        label="Category"
        size="small"
        select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        fullWidth
        SelectProps={{ native: true }}
      >
        {COMPOSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </TextField>
      <TextField label="Subject" size="small" value={subjectTemplate} onChange={(e) => setSubjectTemplate(e.target.value)} fullWidth required placeholder="e.g. {{client.first_name}}, we need your documents" />
      <TextField
        label="Body (HTML supported, use {{merge.tags}})"
        size="small"
        multiline
        minRows={6}
        value={bodyTemplate}
        onChange={(e) => setBodyTemplate(e.target.value)}
        fullWidth
        required
        placeholder="<p>Hi {{client.first_name}},</p><p>...</p>"
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" size="small" onClick={handleSubmit} disabled={saving || !name.trim()}>
          {saving ? 'Saving…' : tpl ? 'Update' : 'Create'}
        </Button>
        <Button size="small" onClick={onCancel}>Cancel</Button>
        {tpl && (
          <Button size="small" color="error" onClick={() => onDelete(tpl.id)} sx={{ ml: 'auto' }}>
            Delete
          </Button>
        )}
      </Box>
    </Box>
  );
}

function ComposeEmailTemplatesSection() {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const { default: axios } = await import('axios');
      const res = await axios.get('/api/compose-email-templates/', { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(res.data);
    } catch {
      setError('Failed to load compose templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    const token = localStorage.getItem('access_token');
    const { default: axios } = await import('axios');
    if (selectedId) {
      await axios.patch(`/api/compose-email-templates/${selectedId}/`, data, { headers: { Authorization: `Bearer ${token}` } });
    } else {
      await axios.post('/api/compose-email-templates/', data, { headers: { Authorization: `Bearer ${token}` } });
    }
    setSelectedId(null);
    setCreating(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    const token = localStorage.getItem('access_token');
    const { default: axios } = await import('axios');
    await axios.delete(`/api/compose-email-templates/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
    setSelectedId(null);
    setCreating(false);
    load();
  };

  const selectedTpl = templates.find((t) => t.id === selectedId) || null;

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>Compose Email Templates</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These templates appear in the email compose window. Use merge tags like {`{{client.first_name}}`} and {`{{firm.name}}`}.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ width: 220, flexShrink: 0 }}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => { setSelectedId(null); setCreating(true); }}
            sx={{ mb: 1 }}
          >
            New Template
          </Button>
          {loading ? (
            [1, 2, 3].map((i) => <Skeleton key={i} height={40} sx={{ mb: 0.5 }} />)
          ) : (
            <List dense disablePadding>
              {templates.map((t) => (
                <ListItemButton
                  key={t.id}
                  selected={selectedId === t.id}
                  onClick={() => { setSelectedId(t.id); setCreating(false); }}
                  sx={{ borderRadius: 1.5, mb: 0.25 }}
                >
                  <ListItemText
                    primary={t.name}
                    secondary={COMPOSE_CATEGORIES.find((c) => c.value === t.category)?.label}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600, noWrap: true }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </ListItemButton>
              ))}
              {templates.length === 0 && !loading && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                  No templates yet.
                </Typography>
              )}
            </List>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          {(creating || selectedId) ? (
            <ComposeTemplateForm
              key={selectedId || 'new'}
              tpl={selectedTpl}
              onSave={handleSave}
              onDelete={handleDelete}
              onCancel={() => { setSelectedId(null); setCreating(false); }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              Select a template to edit, or create a new one.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const EmailTemplatesSettingsWithCompose = () => (
  <>
    <EmailTemplatesSettings />
    <Box sx={{ px: { xs: 2, sm: 3, md: 5 }, pb: 4 }}>
      <ComposeEmailTemplatesSection />
    </Box>
  </>
);

export { EmailTemplatesSettings, ComposeEmailTemplatesSection };
export default EmailTemplatesSettingsWithCompose;
